import {Toast} from "services/notification";
import {isApiError as isApiErrorTypeGuard} from "services/http/ApiError";

/**
 * Type guard to check whether a given error matches the shape of a serialized error
 * (e.g., from Redux or API responses) containing optional `message` or `code` fields.
 *
 * @param error - The unknown error object to check.
 * @returns `true` if the error contains a `message` or `code`, otherwise `false`.
 *
 * @example
 * if (isSerializedError(error)) {
 *   alert(error.message);
 * }
 */
export const isSerializedError = (
  error: unknown,
): error is {message?: string; code?: string} => {
  return typeof error === "object" && error != null && "message" in error;
};

/**
 * Type guard to check if an unknown error is a native JavaScript `Error` object.
 *
 * This includes instances of built-in error types like:
 * - `Error`
 * - `TypeError`
 * - `SyntaxError`
 * - `ReferenceError`
 * - `RangeError`
 * etc.
 *
 * Useful when handling errors from `try/catch` blocks or other sources
 * where the type of the error is unknown.
 *
 * @param error - The value to check.
 * @returns `true` if the value is a native `Error` instance, otherwise `false`.
 *
 * @example
 * try {
 *   doSomethingRisky()
 * } catch (err) {
 *   if (isNativeError(err)) {
 *     console.error('Native error:', err.message)
 *   }
 * }
 */
export const isNativeError = (error: unknown): error is Error => {
  return error instanceof Error;
};

/**
 * Type guard to check if an unknown error is an ApiError object.
 * Re-exports the isApiError function from the ApiError module.
 *
 * @param error - The unknown error object to check
 * @returns `true` if the error is an ApiError, otherwise `false`
 *
 * @example
 * if (isApiError(error)) {
 *   console.error('API error:', error.status, error.message);
 * }
 */
export const isApiError = isApiErrorTypeGuard;

/**
 * Handles various error types and displays a user-friendly error message using `notification`.
 *
 * This utility gracefully supports:
 * - `ApiError` from failed API requests
 * - Serialized errors (objects with `message` and/or `code` fields)
 * - Native JavaScript `Error` instances
 * - Falls back to a generic message if none of the above match
 *
 * Also provides an optional `reset` callback, which is triggered when the feedback message is dismissed.
 *
 * @param error - The error object to handle. Can be of any type.
 * @param reset - Optional callback to run after the feedback toast is closed (e.g., reset form state).
 *
 * @example
 * try {
 *   await someApiCall()
 * } catch (error) {
 *   handleError(error, () => form.resetSubmissionStatus())
 * }
 *
 * @see isApiError
 * @see isSerializedError
 * @see isNativeError
 */
export const handleError = (error: unknown) => {
  let message = "Something went wrong";

  if (isApiError(error)) {
    // Handle ApiError-specific error structure
    message = error.userMessage;
  } else if (isSerializedError(error)) {
    message = error.message || message;
  } else if (isNativeError(error)) {
    message = error.message || message;
  }

  Toast.error(message);
};
